import React, { useState, useEffect } from 'react';
import { FiUserCheck, FiPlus, FiTrash2, FiKey, FiMail, FiPhone, FiCalendar, FiRefreshCw, FiSettings, FiCheck } from 'react-icons/fi';
import EventService from '../services/EventService';

const StaffManagement = () => {
    const [staffList, setStaffList] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // Event Management Modal State (Assign / Revoke)
    const [managingStaff, setManagingStaff] = useState(null);
    const [manageEventIds, setManageEventIds] = useState([]);
    const [manageSaving, setManageSaving] = useState(false);

    // New Staff State (Supports Multi-Event Selection)
    const [newStaff, setNewStaff] = useState({
        staffId: '',
        name: '',
        email: '',
        phone: '',
        password: '',
    });
    const [selectedCreateEventIds, setSelectedCreateEventIds] = useState([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const staffRes = await EventService.getStaffList().catch(() => null);
            
            // Try getActiveEvents endpoint first
            let activeEventsRes = await EventService.getActiveEvents().catch(() => null);
            if (!activeEventsRes || (!activeEventsRes.events && !Array.isArray(activeEventsRes))) {
                activeEventsRes = await EventService.getAllPartnerEvents().catch(() => null);
            }

            const sList = staffRes?.staffList || staffRes?.data?.staffList || (Array.isArray(staffRes) ? staffRes : []);
            
            // Extract raw list
            let rawEvList = activeEventsRes?.events || activeEventsRes?.data?.events || (Array.isArray(activeEventsRes) ? activeEventsRes : []);
            if (!Array.isArray(rawEvList)) rawEvList = [];

            // Format titles and venues properly from nested schema
            const formattedEvList = rawEvList.map(ev => ({
                _id: ev._id ? ev._id.toString() : ev.id,
                title: ev.title || ev.basic?.title || 'Untitled Event',
                venueName: ev.venueName || ev.location?.venueName || ev.location?.address || 'Venue',
                expiryDate: ev.expiryDate || ev.schedule?.endAt || ev.endAt,
                startDate: ev.startDate || ev.schedule?.startAt,
            }));

            // Filter out expired events (end/expiry/start date must be >= today - 24h)
            const now = new Date();
            const filteredEvents = formattedEvList.filter(ev => {
                const dateStr = ev.expiryDate || ev.startDate;
                if (!dateStr) return true;
                return new Date(dateStr) >= new Date(now.getTime() - 24 * 60 * 60 * 1000);
            });

            setStaffList(Array.isArray(sList) ? sList : []);
            setEvents(filteredEvents);

            if (filteredEvents.length > 0 && selectedCreateEventIds.length === 0) {
                setSelectedCreateEventIds([filteredEvents[0]._id]);
            }
        } catch (err) {
            console.error('Error loading staff data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreateStaff = async () => {
        if (!newStaff.name.trim() || !newStaff.password.trim()) {
            setErrorMsg('Staff Name and Password are required.');
            return;
        }

        if (selectedCreateEventIds.length === 0 && events.length > 0) {
            setErrorMsg('Please select at least one assigned event for this staff member.');
            return;
        }

        setSaving(true);
        setErrorMsg('');

        try {
            const payload = {
                staffId: newStaff.staffId.trim() || `STF-${Math.floor(1000 + Math.random() * 9000)}`,
                name: newStaff.name.trim(),
                email: newStaff.email.trim(),
                phone: newStaff.phone.trim(),
                password: newStaff.password.trim(),
                passcode: newStaff.password.trim(),
                assignedEvents: selectedCreateEventIds.length > 0 ? selectedCreateEventIds : (events.map(e => e._id)),
            };

            const res = await EventService.createStaff(payload);
            if (res?.success) {
                setShowAddModal(false);
                setNewStaff({ staffId: '', name: '', email: '', phone: '', password: '' });
                fetchData();
            } else {
                setErrorMsg(res?.message || 'Failed to create staff member.');
            }
        } catch (err) {
            console.error('Error creating staff:', err);
            setErrorMsg(err?.response?.data?.message || err?.message || 'Failed to create staff member.');
        } finally {
            setSaving(false);
        }
    };

    const handleOpenManageEvents = (staff) => {
        setManagingStaff(staff);
        const currentIds = (staff.assignedEvents || []).map(e => (typeof e === 'object' ? e._id : e));
        setManageEventIds(currentIds);
    };

    const handleSaveManagedEvents = async () => {
        if (!managingStaff) return;
        setManageSaving(true);
        try {
            await EventService.updateStaffEvents(managingStaff.id, manageEventIds);
            setManagingStaff(null);
            fetchData();
        } catch (err) {
            console.error('Error updating staff events:', err);
            alert('Failed to update event assignments.');
        } finally {
            setManageSaving(false);
        }
    };

    const handleDeleteStaff = async (id, name) => {
        if (!window.confirm(`Are you sure you want to revoke staff access for "${name}"?`)) return;
        try {
            await EventService.deleteStaff(id);
            setStaffList(prev => prev.filter(s => s.id !== id));
        } catch (err) {
            console.error('Error deleting staff:', err);
        }
    };

    const toggleCreateEventSelection = (eventId) => {
        setSelectedCreateEventIds(prev =>
            prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
        );
    };

    const toggleManageEventSelection = (eventId) => {
        setManageEventIds(prev =>
            prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
        );
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6 text-left pb-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
                <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                        <FiUserCheck className="text-purple-700" /> Event Staff & Gate Scanner Access
                    </h1>
                    <p className="text-xs font-medium text-slate-500 mt-1">Assign gate volunteers & staff members with Staff ID access for active non-expired events</p>
                </div>
                <div className="flex items-center gap-2 self-start md:self-auto">
                    <button
                        onClick={fetchData}
                        className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200 text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
                        title="Refresh List"
                    >
                        <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                    </button>
                    <button
                        onClick={() => {
                            setErrorMsg('');
                            if (events.length > 0) setSelectedCreateEventIds([events[0]._id]);
                            setShowAddModal(true);
                        }}
                        className="px-4 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2"
                    >
                        <FiPlus className="w-4 h-4" /> + Create Event Staff
                    </button>
                </div>
            </div>

            {/* Staff Grid */}
            {loading ? (
                <div className="p-12 text-center text-slate-500 text-xs space-y-3 bg-white border border-slate-200 rounded-2xl shadow-sm">
                    <FiRefreshCw className="w-6 h-6 animate-spin mx-auto text-purple-700" />
                    <p>Loading active event staff members...</p>
                </div>
            ) : staffList.length === 0 ? (
                <div className="p-12 text-center text-slate-500 text-xs space-y-3 bg-white border border-slate-200 rounded-2xl shadow-sm">
                    <FiUserCheck className="w-10 h-10 mx-auto text-slate-300" />
                    <p className="font-extrabold text-slate-900 text-sm">No Event Staff Created Yet</p>
                    <p className="text-xs text-slate-500 font-medium max-w-md mx-auto">
                        Create staff accounts for your gate volunteers so they can log in to the Tapcoe Staff App and scan ticket entries.
                    </p>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="mt-2 px-4 py-2.5 rounded-xl bg-purple-700 text-white font-bold text-xs shadow-sm hover:bg-purple-800 transition"
                    >
                        + Create First Staff Account
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {staffList.map((s) => {
                        const assignedEvList = Array.isArray(s.assignedEvents) ? s.assignedEvents : [];
                        return (
                            <div key={s.id} className="p-5 rounded-2xl bg-white border border-slate-200 space-y-4 relative hover:border-purple-300 transition-all flex flex-col justify-between shadow-sm">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-slate-900">{s.name}</h3>
                                                <span className="px-2 py-0.5 rounded bg-purple-50 text-purple-900 font-mono text-[10px] font-bold border border-purple-200">
                                                    {s.staffId}
                                                </span>
                                            </div>
                                            <span className="text-[10px] text-slate-500 font-medium block mt-0.5">{s.role || 'Gate Staff'}</span>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-extrabold">
                                            {s.status}
                                        </span>
                                    </div>

                                    <div className="text-xs text-slate-600 font-medium space-y-1">
                                        {s.email && s.email !== 'N/A' && (
                                            <div className="flex items-center gap-1.5"><FiMail className="text-slate-400 w-3.5 h-3.5" /> {s.email}</div>
                                        )}
                                        {s.phone && s.phone !== 'N/A' && (
                                            <div className="flex items-center gap-1.5"><FiPhone className="text-slate-400 w-3.5 h-3.5" /> {s.phone}</div>
                                        )}
                                    </div>

                                    {/* Assigned Events Chips */}
                                    <div className="pt-2 border-t border-slate-100 space-y-1.5">
                                        <div className="flex items-center justify-between text-[11px]">
                                            <span className="text-slate-700 font-bold flex items-center gap-1">
                                                <FiCalendar className="text-purple-700" /> Assigned Active Events:
                                            </span>
                                            <button
                                                onClick={() => handleOpenManageEvents(s)}
                                                className="text-[10px] text-purple-900 hover:text-purple-700 font-bold flex items-center gap-1 px-2 py-0.5 rounded bg-purple-50 border border-purple-200"
                                            >
                                                <FiSettings className="w-3 h-3 text-purple-700" /> Assign / Revoke
                                            </button>
                                        </div>

                                        <div className="flex flex-wrap gap-1.5 pt-1">
                                            {assignedEvList.length === 0 ? (
                                                <span className="text-[10px] text-amber-700 font-bold italic">No event assigned yet</span>
                                            ) : (
                                                assignedEvList.map(ev => (
                                                    <span key={ev._id || ev.id} className="px-2 py-1 rounded-lg bg-slate-50 text-purple-900 border border-slate-200 text-[10px] font-bold">
                                                        📍 {ev.title || 'Event'}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                                    <span className="text-slate-600 font-mono font-medium flex items-center gap-1">
                                        <FiKey className="text-slate-400" /> App Passcode: <strong className="text-purple-900 font-bold">{s.passcode || '******'}</strong>
                                    </span>
                                    <button
                                        onClick={() => handleDeleteStaff(s.id, s.name)}
                                        className="text-rose-700 hover:text-rose-800 p-1.5 rounded-lg hover:bg-rose-50 transition-colors flex items-center gap-1 text-[11px] font-bold"
                                        title="Revoke Staff Access"
                                    >
                                        <FiTrash2 className="w-3.5 h-3.5" /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── CREATE STAFF MODAL ── */}
            {showAddModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl max-w-lg w-full space-y-4 shadow-xl max-h-[90vh] overflow-y-auto text-left">
                        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                                <FiUserCheck className="text-purple-700" /> Create Event Staff Account
                            </h3>
                            <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>

                        {errorMsg && (
                            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold">
                                {errorMsg}
                            </div>
                        )}

                        <div className="space-y-3">
                            <div>
                                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">Staff ID (For Mobile App Login)</label>
                                <input
                                    type="text"
                                    placeholder="e.g. STF-101 (Auto-generated if empty)"
                                    value={newStaff.staffId}
                                    onChange={e => setNewStaff({ ...newStaff, staffId: e.target.value })}
                                    className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">Full Name *</label>
                                <input
                                    type="text"
                                    placeholder="Staff Member Name"
                                    value={newStaff.name}
                                    onChange={e => setNewStaff({ ...newStaff, name: e.target.value })}
                                    className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">App Password / Passcode *</label>
                                <input
                                    type="text"
                                    placeholder="Set App Password (e.g. pass123)"
                                    value={newStaff.password}
                                    onChange={e => setNewStaff({ ...newStaff, password: e.target.value })}
                                    className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">Email (Optional)</label>
                                    <input
                                        type="email"
                                        placeholder="staff@email.com"
                                        value={newStaff.email}
                                        onChange={e => setNewStaff({ ...newStaff, email: e.target.value })}
                                        className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">Phone (Optional)</label>
                                    <input
                                        type="text"
                                        placeholder="+91 9876543210"
                                        value={newStaff.phone}
                                        onChange={e => setNewStaff({ ...newStaff, phone: e.target.value })}
                                        className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                                    />
                                </div>
                            </div>

                            {/* Active Non-Expired Events Selection */}
                            <div>
                                <label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block mb-1">
                                    Assign Active Events (Select One or Multiple) *
                                </label>

                                {events.length === 0 ? (
                                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold">
                                        No active non-expired events found. Staff can be created and events assigned later.
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-40 overflow-y-auto p-2 bg-slate-50 rounded-xl border border-slate-200">
                                        {events.map(ev => {
                                            const isSelected = selectedCreateEventIds.includes(ev._id);
                                            return (
                                                <div
                                                    key={ev._id}
                                                    onClick={() => toggleCreateEventSelection(ev._id)}
                                                    className={`p-2.5 rounded-lg flex items-center justify-between cursor-pointer text-xs transition-all ${
                                                        isSelected ? 'bg-purple-100 border border-purple-300 text-purple-950 font-bold' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                                                    }`}
                                                >
                                                    <div>
                                                        <p className="font-bold">{ev.title}</p>
                                                        <p className="text-[10px] text-slate-500 font-medium">{ev.venueName || 'Venue'}</p>
                                                    </div>
                                                    <div className={`w-5 h-5 rounded flex items-center justify-center border ${
                                                        isSelected ? 'bg-purple-700 border-purple-700 text-white' : 'border-slate-300'
                                                    }`}>
                                                        {isSelected && <FiCheck className="w-3.5 h-3.5" />}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={saving}
                                onClick={handleCreateStaff}
                                className="px-5 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white text-xs font-bold transition-all shadow-sm"
                            >
                                {saving ? 'Creating...' : 'Create Staff Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MANAGE / ASSIGN / REVOKE EVENTS MODAL ── */}
            {managingStaff && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                    <div className="bg-white border border-slate-200 p-6 rounded-2xl max-w-md w-full space-y-4 shadow-xl text-left">
                        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                            <div>
                                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                                    <FiSettings className="text-purple-700" /> Manage Assigned Events
                                </h3>
                                <p className="text-[11px] text-slate-500 font-medium mt-0.5">Staff: <strong className="text-slate-900">{managingStaff.name}</strong> ({managingStaff.staffId})</p>
                            </div>
                            <button onClick={() => setManagingStaff(null)} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>

                        <p className="text-xs text-slate-700 font-medium">
                            Check to <strong>Assign</strong> or uncheck to <strong>Revoke</strong> scanning permissions for active non-expired events:
                        </p>

                        <div className="space-y-2 max-h-56 overflow-y-auto p-2 bg-slate-50 rounded-xl border border-slate-200">
                            {events.length === 0 ? (
                                <p className="text-xs text-slate-500 p-2 font-medium">No active non-expired partner events found.</p>
                            ) : (
                                events.map(ev => {
                                    const isSelected = manageEventIds.includes(ev._id);
                                    return (
                                        <div
                                            key={ev._id}
                                            onClick={() => toggleManageEventSelection(ev._id)}
                                            className={`p-2.5 rounded-lg flex items-center justify-between cursor-pointer text-xs transition-all ${
                                                isSelected ? 'bg-purple-100 border border-purple-300 text-purple-950 font-bold' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                                            }`}
                                        >
                                            <div>
                                                <p className="font-bold">{ev.title}</p>
                                                <p className="text-[10px] text-slate-500 font-medium">{ev.venueName || 'Venue'}</p>
                                            </div>
                                            <div className={`w-5 h-5 rounded flex items-center justify-center border ${
                                                isSelected ? 'bg-purple-700 border-purple-700 text-white' : 'border-slate-300'
                                            }`}>
                                                {isSelected && <FiCheck className="w-3.5 h-3.5" />}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button
                                onClick={() => setManagingStaff(null)}
                                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={manageSaving}
                                onClick={handleSaveManagedEvents}
                                className="px-5 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white text-xs font-bold transition-all shadow-sm"
                            >
                                {manageSaving ? 'Saving...' : 'Save Assignments'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffManagement;
